// lib/studio-develop.ts
import type { StudioDevelopSettings } from '#types/types';

export const DEFAULT_STUDIO_DEVELOP_SETTINGS: StudioDevelopSettings = {
  exposure: 0,
  contrast: 0,
  highlights: 0,
  shadows: 0,
  whites: 0,
  blacks: 0,
  temperature: 0,
  tint: 0,
  vibrance: 0,
  saturation: 0,
  clarity: 0,
  sharpen: 0,
  noiseReduction: 0,
  vignette: 0,
};

export const STUDIO_DEVELOP_PARAM_KEY = 'studioDevelop';

export const STUDIO_DEVELOP_CONTROLS: Array<{
  key: keyof StudioDevelopSettings;
  label: string;
  min: number;
  max: number;
  step: number;
  group: 'Light' | 'Color' | 'Detail' | 'Effects';
}> = [
  { key: 'exposure', label: 'Exposure', min: -5, max: 5, step: 0.05, group: 'Light' },
  { key: 'contrast', label: 'Contrast', min: -100, max: 100, step: 1, group: 'Light' },
  { key: 'highlights', label: 'Highlights', min: -100, max: 100, step: 1, group: 'Light' },
  { key: 'shadows', label: 'Shadows', min: -100, max: 100, step: 1, group: 'Light' },
  { key: 'whites', label: 'Whites', min: -100, max: 100, step: 1, group: 'Light' },
  { key: 'blacks', label: 'Blacks', min: -100, max: 100, step: 1, group: 'Light' },
  { key: 'temperature', label: 'Temperature', min: -100, max: 100, step: 1, group: 'Color' },
  { key: 'tint', label: 'Tint', min: -100, max: 100, step: 1, group: 'Color' },
  { key: 'vibrance', label: 'Vibrance', min: -100, max: 100, step: 1, group: 'Color' },
  { key: 'saturation', label: 'Saturation', min: -100, max: 100, step: 1, group: 'Color' },
  { key: 'clarity', label: 'Clarity', min: -100, max: 100, step: 1, group: 'Detail' },
  { key: 'sharpen', label: 'Sharpen', min: 0, max: 100, step: 1, group: 'Detail' },
  { key: 'noiseReduction', label: 'Noise Reduction', min: 0, max: 100, step: 1, group: 'Detail' },
  { key: 'vignette', label: 'Vignette', min: -100, max: 100, step: 1, group: 'Effects' },
];

const clamp = (value: number, min = 0, max = 1): number => Math.max(min, Math.min(max, value));

const toFiniteNumber = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return fallback;
};

export const normalizeStudioDevelopSettings = (value: unknown): StudioDevelopSettings => {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Partial<Record<keyof StudioDevelopSettings, unknown>>
    : {};

  return STUDIO_DEVELOP_CONTROLS.reduce<StudioDevelopSettings>((acc, control) => {
    const rawValue = toFiniteNumber(source[control.key], DEFAULT_STUDIO_DEVELOP_SETTINGS[control.key]);
    acc[control.key] = Math.max(control.min, Math.min(control.max, rawValue));
    return acc;
  }, { ...DEFAULT_STUDIO_DEVELOP_SETTINGS });
};

export const areStudioDevelopSettingsEqual = (
  leftInput: StudioDevelopSettings,
  rightInput: StudioDevelopSettings
): boolean => {
  const left = normalizeStudioDevelopSettings(leftInput);
  const right = normalizeStudioDevelopSettings(rightInput);
  return STUDIO_DEVELOP_CONTROLS.every((control) => Math.abs(left[control.key] - right[control.key]) < 0.0001);
};

export const areStudioDevelopSettingsDefault = (settings: StudioDevelopSettings): boolean =>
  STUDIO_DEVELOP_CONTROLS.every((control) => Math.abs(settings[control.key] - DEFAULT_STUDIO_DEVELOP_SETTINGS[control.key]) < 0.0001);

const blurRgb3x3 = (source: Uint8ClampedArray | Float32Array, width: number, height: number): Float32Array => {
  const output = new Float32Array(source.length);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let r = 0;
      let g = 0;
      let b = 0;
      let samples = 0;

      for (let oy = -1; oy <= 1; oy += 1) {
        const py = y + oy;
        if (py < 0 || py >= height) continue;
        for (let ox = -1; ox <= 1; ox += 1) {
          const px = x + ox;
          if (px < 0 || px >= width) continue;
          const sampleIndex = (py * width + px) * 4;
          r += source[sampleIndex] ?? 0;
          g += source[sampleIndex + 1] ?? 0;
          b += source[sampleIndex + 2] ?? 0;
          samples += 1;
        }
      }

      const index = (y * width + x) * 4;
      output[index] = r / samples;
      output[index + 1] = g / samples;
      output[index + 2] = b / samples;
      output[index + 3] = source[index + 3] ?? 255;
    }
  }

  return output;
};

const applyLocalContrast = (
  base: Float32Array,
  width: number,
  height: number,
  amount: number
): Float32Array => {
  if (Math.abs(amount) < 0.0001) return base;
  const blurred = blurRgb3x3(base, width, height);
  const output = new Float32Array(base.length);

  for (let index = 0; index < base.length; index += 4) {
    output[index] = base[index]! + (base[index]! - blurred[index]!) * amount;
    output[index + 1] = base[index + 1]! + (base[index + 1]! - blurred[index + 1]!) * amount;
    output[index + 2] = base[index + 2]! + (base[index + 2]! - blurred[index + 2]!) * amount;
    output[index + 3] = base[index + 3]!;
  }

  return output;
};

export const applyStudioDevelopSettingsToImageData = (
  input: ImageData,
  settingsInput: unknown
): ImageData => {
  const settings = normalizeStudioDevelopSettings(settingsInput);
  const { width, height } = input;
  const source = input.data;
  const output = new ImageData(width, height);
  const noiseBlend = clamp(settings.noiseReduction / 100, 0, 1) * 0.72;
  const denoised = noiseBlend > 0.0001 ? blurRgb3x3(source, width, height) : null;
  let working: Float32Array<ArrayBufferLike> = new Float32Array(source.length);

  for (let index = 0; index < source.length; index += 4) {
    const r = source[index] ?? 0;
    const g = source[index + 1] ?? 0;
    const b = source[index + 2] ?? 0;
    if (denoised) {
      working[index] = r + ((denoised[index] ?? r) - r) * noiseBlend;
      working[index + 1] = g + ((denoised[index + 1] ?? g) - g) * noiseBlend;
      working[index + 2] = b + ((denoised[index + 2] ?? b) - b) * noiseBlend;
    } else {
      working[index] = r;
      working[index + 1] = g;
      working[index + 2] = b;
    }
    working[index + 3] = source[index + 3] ?? 255;
  }

  const detailAmount = settings.sharpen / 115 + settings.clarity / 220;
  working = applyLocalContrast(working, width, height, detailAmount);

  const exposureFactor = Math.pow(2, settings.exposure);
  const contrastFactor = 1 + settings.contrast / 100;
  const saturationFactor = 1 + settings.saturation / 100;
  const vibranceAmount = settings.vibrance / 100;
  const temp = settings.temperature / 100;
  const tint = settings.tint / 100;
  const highlightAmount = settings.highlights / 100;
  const shadowAmount = settings.shadows / 100;
  const whiteAmount = settings.whites / 100;
  const blackAmount = settings.blacks / 100;
  const vignetteAmount = settings.vignette / 100;
  const centerX = (width - 1) / 2;
  const centerY = (height - 1) / 2;
  const maxDistance = Math.max(1, Math.sqrt(centerX * centerX + centerY * centerY));

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      let r = (working[index] ?? 0) / 255;
      let g = (working[index + 1] ?? 0) / 255;
      let b = (working[index + 2] ?? 0) / 255;

      r *= exposureFactor;
      g *= exposureFactor;
      b *= exposureFactor;

      let luminance = clamp(0.2126 * r + 0.7152 * g + 0.0722 * b);
      const shadowWeight = Math.pow(1 - luminance, 2);
      const highlightWeight = Math.pow(luminance, 2);
      const blackWeight = Math.pow(1 - luminance, 4);
      const whiteWeight = Math.pow(luminance, 4);
      const tonalOffset =
        shadowAmount * shadowWeight * 0.42 +
        highlightAmount * highlightWeight * 0.35 +
        whiteAmount * whiteWeight * 0.28 +
        blackAmount * blackWeight * 0.28;

      r += tonalOffset;
      g += tonalOffset;
      b += tonalOffset;

      r += temp * 0.085 + tint * 0.03;
      g -= tint * 0.055;
      b -= temp * 0.085 + tint * -0.03;

      r = (r - 0.5) * contrastFactor + 0.5;
      g = (g - 0.5) * contrastFactor + 0.5;
      b = (b - 0.5) * contrastFactor + 0.5;

      luminance = clamp(0.2126 * r + 0.7152 * g + 0.0722 * b);
      const maxChannel = Math.max(r, g, b);
      const minChannel = Math.min(r, g, b);
      const existingSaturation = clamp(maxChannel - minChannel);
      const vibranceFactor = 1 + vibranceAmount * (1 - existingSaturation) * 0.85;
      const colorFactor = saturationFactor * vibranceFactor;

      r = luminance + (r - luminance) * colorFactor;
      g = luminance + (g - luminance) * colorFactor;
      b = luminance + (b - luminance) * colorFactor;

      if (Math.abs(vignetteAmount) > 0.0001) {
        const dx = x - centerX;
        const dy = y - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy) / maxDistance;
        const edge = clamp((distance - 0.32) / 0.68);
        const edgeSmooth = edge * edge * (3 - 2 * edge);
        const factor = 1 + vignetteAmount * edgeSmooth * 0.7;
        r *= factor;
        g *= factor;
        b *= factor;
      }

      output.data[index] = Math.round(clamp(r) * 255);
      output.data[index + 1] = Math.round(clamp(g) * 255);
      output.data[index + 2] = Math.round(clamp(b) * 255);
      output.data[index + 3] = source[index + 3] ?? 255;
    }
  }

  return output;
};
